import { WeightedReturnType } from '../../../features/performance/performance.domain';
import { Currency } from '../../../domain/market.domain';
import { AccountId, CurrencyValueMap, DescribableAccount, SortableAccount } from '../../../domain/account.domain';
import { AccountsAndTotalsGroupedByNumberType, AggregationTypeAware } from '../../../domain/balances.domain';
import { HomePagePrimaryTabConfiguration, HOME_PAGE_BALANCE_TREND_PRIMARY_TAB } from '../../../domain/home-page-balance.domain';

export {
  HomePagePrimaryTabConfiguration,
  HOME_PAGE_BALANCE_TREND_PRIMARY_TAB
};

export type DataPoints = Array<DataPoint>;

export enum PeriodType {
  LAST_DAY = 'LAST_DAY',
  LAST_MONTH = 'LAST_MONTH',
  LAST_3_YEARS = 'LAST_3_YEARS'
}

export interface DataPoint {
  dataPointKey: {
    accountType: boolean;
    key: string;
  };
  date: Date;
  equity: number;
  equityPercentChange: number;
  equityValueChange: number;
}

export type EquitiesChange = Record<string, Record<string, EquityChange>>;

export interface EquityChange {
  equity: number;
  equityValueChange: number;
  equityPercentChange: number;
}

export type AccountsIncome = Record<string, Record<string, AccountIncome>>;

export interface AccountIncome {
  incomeAmount: number;
}

export class ConsolidatedBalance {
  private static readonly DEFAULT_CURRENCY: Currency = Currency.CAD;

  constructor(public asOfDate: Date,
              public usdRate: number, // this is IBM 'USD' -> 'CAD' rate
              public totalCashBalance: CurrencyValueMap,
              public totalSecurityBalance: CurrencyValueMap,
              public totalBalance: CurrencyValueMap) {}

  getCashBalance(): number | undefined {
    return this.totalCashBalance[ConsolidatedBalance.DEFAULT_CURRENCY].amount;
  }

  getSecurityBalance(): number | undefined {
    return this.totalSecurityBalance[ConsolidatedBalance.DEFAULT_CURRENCY].amount;
  }

  getTotalBalance(): number | undefined {
    return this.totalBalance[ConsolidatedBalance.DEFAULT_CURRENCY].amount;
  }
}

export type TypeSummary = DescribableAccount & SortableAccount & {
  id: AccountId;
  currency: Currency;
  balanceDate?: Date;
  totalBalance?: CurrencyValueMap;
};

// AccountSummary is the final type used in template:
// ConsolidatedBalance and TypeSummary both get merged & converted into it.
export type AccountSummary = Pick<TypeSummary,
'id' |
'currency' |
'number' |
'groupNumber' |
'businessLineDescription' |
'accountTypeDescription' |
'type' |
'totalBalance' |
'accountPlatform' > & {
  equityChange: EquityChange;
  accountIncome: AccountIncome;
};

export interface BalancesSummary {
  consolidatedBalance: ConsolidatedBalance;
  typeSummaries: Array<TypeSummary>;
}

/**
 * @see BalanceTotals in balances.domain.ts
 */
export interface ConsolidatedBalanceResponse {
  asOfDate: Date;
  usdRate: number;
  totalCashBalance: CurrencyValueMap;
  totalSecurityBalance: CurrencyValueMap;
  totalBalance: CurrencyValueMap;
}

export interface PortfolioInvestmentResponse extends AggregationTypeAware {
  result: ConsolidatedBalanceResponse;
}

export interface BalancesSummaryResponse {
  ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE: AccountsAndTotalsGroupedByNumberType;
  PORTFOLIO_INVESTMENT_TYPE_TOTAL: PortfolioInvestmentResponse;
}

export interface PortfolioPerformance {
  dataPoints: DataPoints;
  equityChange: EquityChange;
  weightedReturnType: WeightedReturnType;
}

export enum ChartYAxisUnit {
  NUMBER = 'number',
  CURRENCY = 'currency'
}

export interface BalanceTrendChartExtraConfig {
  isFullWidth: boolean;
  hasVerticalTrail: boolean;
  hideYAxisGridLine: boolean;
  yAxisUnit: ChartYAxisUnit;
  hoverToShowSeriesMarker: boolean;
  useSecondaryLinearGradient: boolean;
  useSecondarySeriesLineColor: boolean;
  pointMouseOver?: (event: MouseEvent) => void;
  pointMouseOut?: (event: MouseEvent) => void;
}

